import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { setOptions, localeDe, MbscModule } from '@mobiscroll/angular';
import { CommonModule } from '@angular/common';
import {
  catchError,
  lastValueFrom,
  Observable,
  of,
  Subscription,
  tap,
} from 'rxjs';
import { AuthService } from '../../../services/auth/service/auth.service';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';

setOptions({
  locale: localeDe,
  theme: 'ios',
  themeVariant: 'light',
});
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, RouterModule, MbscModule, CommonModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  private loginSubscription!: Subscription;
  apiUrl = environment.apiUrl;
  testUrl = 'https://test.api.tridimo.de/api/';
  ProductionUrl = 'https://api.tridimo.de/api/';
  devUrl = 'https://dev.api.tridimo.de/api/';
  authService = inject(AuthService);
  router = inject(Router);
  formIsVAlid: boolean = false;
  testEnvironment: boolean = false;
  private sessionCheckInProgress = false;

  protected loginForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
  });

  constructor(private http: HttpClient) {}

  async ngOnInit() {
    if (this.apiUrl !== this.devUrl) {
      this.testEnvironment = false;

      try {
        const data: any = await this.fetchUserDataWithTimeout(15000);
        console.log('UserData: ', data);
        if (data) {
          sessionStorage.setItem('firstName', data.firstName);
          sessionStorage.setItem('lastName', data.lastName);
          sessionStorage.setItem('role', data.role);
          sessionStorage.setItem('permissions', data.permissions);
          // sessionStorage.setItem('roleRoute', 'main-route');

          localStorage.setItem('firstName', data.firstName);
          localStorage.setItem('lastName', data.lastName);
          localStorage.setItem('role', data.role);
        } else {
          console.error('User-Daten sind nicht vorhanden');
        }

        if (data && this.authService.isLoggedIn()) {
          this.checkSessionTimeout();
          this.redirectAfterLogin(data.role);
        }
      } catch (err: any) {
        //this.showNotificationError('Login', err?.message || 'Server schickt keine Antwort');
        console.log(err);
        console.error('Fehler beim Laden der User-Daten:', err);
        console.error('Fehlerdetails:', {
          message: err?.message,
          status: err?.status,
          url: err?.url,
          error: err?.error,
        });
        setTimeout(() => this.authService.logout(), 1000);
      }
    } else {
      this.testEnvironment = true;
    }
  }

  private fetchUserDataWithTimeout(timeoutMs: number): Promise<any> {
    return Promise.race([
      this.fetchUserData(),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error('Timeout: Server schickt keine Antwort')),
          timeoutMs,
        ),
      ),
    ]);
  }

  checkSessionTimeout() {
    console.log('checkSessionTimeout wird ausgeführt');
    if (!this.isLoggedIn()) {
      this.sessionCheckInProgress = false;
      return;
    }

    this.checkSession().subscribe({
      next: (response: any) => {
        console.log('Sitzung', response);
        if (response.status === 401 || response.statusText === 'Unauthorized') {
          this.authService.logout();
        }
        this.sessionCheckInProgress = false;
      },
      error: (err) => {
        console.log('Sitzung abgelaufen, Benutzer ausgeloggt', err);
        this.authService.logout();
        this.sessionCheckInProgress = false;
      },
    });
  }

  private checkSession(): Observable<any> {
    return this.http
      .get(`${this.apiUrl}user/details`, {
        withCredentials:
          this.apiUrl === this.testUrl || this.apiUrl === this.ProductionUrl,
      })
      .pipe(catchError((error) => of(error)));
  }

  private redirectAfterLogin(role: string): void {
    const adminRoles = [
      'Admin',
      'Buero',
      'Personal',
      'Teamleitung',
      'Ausbildung',
    ];

    if (adminRoles.includes(role)) {
      void this.router.navigate(['/admin-calendar']);
      return;
    }

    if (role === 'Dolmetscher') {
      void this.router.navigate(['/interpreter-calendar']);
    }
  }

  async fetchUserData() {
    const response = await lastValueFrom(
      this.http
        .get(`${this.apiUrl}user/details`, { withCredentials: true })
        .pipe(
          tap((result: any) => {
            const user = {
              user: result,
            };

            localStorage.setItem('authUser', JSON.stringify(user));
            //localStorage.setItem('authUser', JSON.stringify(result));
          }),
        ),
    );
    return response;
  }

  isLoggedIn() {
    return localStorage.getItem('authUser') !== null;
  }

  onSubmit() {
    if (this.loginForm.valid) {
      this.authService.login(this.loginForm.value).subscribe(
        (data: any) => {
          sessionStorage.setItem('authToken', data.token);
          const userData = {
            active: data.user.active,
            email: data.user.email,
            firstName: data.user.firstName,
            id: data.user.id,
            kuerzel: data.user.kuerzel,
            lastLogin: data.user.lastLogin,
            lastName: data.user.lastName,
            role: data.user.role,
            startDate: data.user.startDate,
          };

          sessionStorage.setItem('role', data.user.role);
          sessionStorage.setItem('firstName', data.user.firstName);
          sessionStorage.setItem('lastName', data.user.lastName);
          //sessionStorage.setItem('id','4');

          localStorage.setItem('role', data.user.role);
          localStorage.setItem('firstName', data.user.firstName);
          localStorage.setItem('lastName', data.user.lastName);
          sessionStorage.setItem('roleRoute', 'main-route');
          sessionStorage.setItem('permissions', data.user.permissions);
          if (this.authService.isLoggedIn()) {
            this.redirectAfterLogin(data.user.role);
          }
        },
        (error) => {
          if (error.status === 400) {
            console.log(
              'Login',
              'Ungültige Anmeldedaten. Bitte überprüfen deine Eingaben.',
            );
          } else {
            console.log(
              'Login',
              'Ein unbekannter Fehler ist aufgetreten. Bitte versuche es später erneut oder kontaktiere den Support.',
            );
          }
        },
      );
    } else {
      console.log('Login', 'Bitte fülle das Formular aus.');
    }
  }
}
